import { ThemeEvents, QuantitySelectorUpdateEvent } from '@jky/events';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { debounce, fetchConfig, onAnimationEnd } from '@jky/utilities';
import { Component } from '@jky/component';

if (!customElements.get('jky-cart')) {
    customElements.define('jky-cart', class extends Component {
        requiredRefs = ['drawer', 'trigger'];
        #debouncedOnChange = debounce((event) => {
            if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
        }, 300);
        pendingCartFetch = null;
        #affirmRetryCount = 0;
        #affirmScriptPromise = null;

        connectedCallback() {
            super.connectedCallback();
            this.mainClass = '.jky-cart-main';
            this.cartItemCountClass = '.title_cartItemCount';
            this.routes = {
                cart_change_url: this.dataset.routesChange,
                cart_update_url: this.dataset.routesUpdate,
                cart_url: this.dataset.routesCart
            }
            this.quantity_max_tips = this.dataset.quantityMaxTips;
            this.active_parent_line = this.dataset.active_parent_line === 'true';
            this.active_affirm = this.dataset.activeAffirm === 'true';

            document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
            document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
            this.refreshAffirm();
        }
        
        disconnectedCallback() {
            super.disconnectedCallback();
            document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
            document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
        }

        /**
         * 处理QuantitySelectorUpdateEvent事件
         * @param {QuantitySelectorUpdateEvent} event - 事件对象
         */
        #onQuantityChange(event) {
            if (!(event.target instanceof Node) || !this.contains(event.target)) return;
            const { quantity, cartLine: line } = event.detail;
            // 确保购物车line存在
            if (!line) return;
            if (quantity === 0) {
                return this.onLineItemRemove(line);
            }
            this.updateQuantity({
                line,
                quantity,
                action: 'change'
            });
        }

        /**
         * 处理购物车更新事件(外部触发)
         *
         * @param {CartLinesUpdateEvent} event
         */
        #handleCartUpdate = (event) => {
            const that = this;
            if (event.target === this) return;
            event.promise?.then(async ({ detail }) => {
                const sections = detail?.sections || {};
                const newSectionHTML = new DOMParser().parseFromString(
                    sections[that.sectionId],
                    'text/html'
                );
                this.onCartUpdate(newSectionHTML);
            })
        };

        /**
         * Updates the quantity.
         * @param {Object} config - The config.
         * @param {number} config.line - The line.
         * @param {number} config.quantity - The quantity.
         * @param {string} config.action - The action.
         */
        updateQuantity(config) {
            this.#disableCartItems();
            const { line, quantity } = config;
            const currentRow = this.refs.cartItemRows?.[line - 1];

            /**
             * 仅当当前行是 BuyXGetY 主行(data-buyXGetY="main")时走 cart/update.js 逻辑，
             * 其余行统一走 cart/change.js 单行更新逻辑。
             */
            if (!this.#isBuyXGetYMainRow(currentRow)) {
                this.#updateQuantityByChange({ line, quantity, action: config.action });
                return;
            }

            let relatedRows = this.#getRelatedCartItemRows(line, quantity);
            try {
                if(currentRow.hasAttribute('data-parent-key') && config.action === 'clear') { // 单独删除子行时，不使用关联行
                    relatedRows = [currentRow];
                }
            } catch(_) {}

            /**
             * 防止找不到 DOM relationship 时影响原有逻辑
             */
            const rows = relatedRows.length ? relatedRows : [this.refs.cartItemRows?.[line - 1]];
            /**
             * 收集需要更新的 line
             * Shopify cart/update.js:
             * {
             *   updates: {
             *      "line-item-key-1": 2,
             *      "line-item-key-2": 2
             *   }
             * }
             */
            const updates = {};
            const updateLines = [];
            rows.forEach((row) => {
                if (!(row instanceof HTMLElement)) return;
                const key = row.dataset.key;
                if (!key) return;
                const numUnit = Number(row?.dataset?.numUnit) || 1;
                updates[key] = quantity * numUnit;
                const rowIndex = Array.from(this.refs.cartItemRows).indexOf(row);
                if (rowIndex !== -1) {
                    updateLines.push({
                        id: key,
                        quantity: quantity * numUnit,
                        line: rowIndex + 1,
                    });
                    this.#handleLineSkeleton(rowIndex + 1);
                }
            });

            /**
             * 如果 relationship 没有找到，
             * 保持原来的单行更新逻辑。
             */
            if (!Object.keys(updates).length) {
                const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
                if (!lineId) {
                    this.#enableCartItems();
                    return;
                }
                updates[lineId] = quantity;
                updateLines.push({
                    id: lineId,
                    quantity,
                    line,
                });
                this.#handleLineSkeleton(line);
            }

            const sectionsToUpdate = this.#getSectionsToUpdate();
            this.#handleLineSkeleton(line);

            const body = JSON.stringify({
                updates,
                sections: Array.from(sectionsToUpdate).join(','),
                sections_url: window.location.pathname
            });
            const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

            this.dispatchEvent(new CartLinesUpdateEvent({
                    action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
                    context: 'cart',
                    lines: updateLines,
                    promise: deferredUpdatePromise.promise,
                })
            );

            let resp_code = 0;
            fetch(`${this.routes.cart_update_url}`, fetchConfig('json', { body }))
                .then((response) => {
                    resp_code = response.status;
                    return response.text();
                })
                .then((responseText) => {
                    if(resp_code === 400) {
                        console.log("变更行错误情况下，更新购物车行数据");
                        this.refreshCart();
                    } else {
                        this.#handleUpdateResponse({ line, deferredUpdatePromise, responseText });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    deferredUpdatePromise.reject(error);
                    this.dispatchEvent(
                        new CartErrorEvent({
                            error: error?.message || 'Failed to update cart',
                            code: 'SERVICE_UNAVAILABLE'
                        })
                    );
                })
                .finally(() => {
                    this.#enableCartItems();
                    this.#handleRemoveLineSkeleton(line);
                });
        }

        /**
         * 判断当前行是否为 BuyXGetY 主行。
         * 主行标记：<tr data-buyXGetY="main">
         *
         * @param {HTMLElement} currentRow - 当前购物车行。
         * @returns {boolean}
         */
        #isBuyXGetYMainRow(currentRow) {
            if (!(currentRow instanceof HTMLElement)) return false;
            return currentRow.getAttribute('data-buyXGetY') === 'main';
        }

        /**
         * 通过 /cart/change.js 更新单行数量。
         * 用于非 BuyXGetY 主行。
         *
         * @param {Object} config - The config.
         * @param {number} config.line - The line.
         * @param {number} config.quantity - The quantity.
         * @param {string} config.action - The action.
         */
        #updateQuantityByChange(config) {
            const { line, quantity, action } = config;
            const currentRow = this.refs.cartItemRows?.[line - 1];
            if (!(currentRow instanceof HTMLElement)) {
                this.#enableCartItems();
                return;
            }

            const lineKey = currentRow.dataset.key ?? '';
            this.#handleLineSkeleton(line);

            const sectionsToUpdate = this.#getSectionsToUpdate();
            const body = JSON.stringify({
                id: lineKey,
                quantity,
                sections: Array.from(sectionsToUpdate).join(','),
                sections_url: window.location.pathname
            });

            const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
            this.dispatchEvent(new CartLinesUpdateEvent({
                    action: action === 'change' && quantity > 0 ? 'update' : 'remove',
                    context: 'cart',
                    lines: [{
                        id: lineKey,
                        quantity,
                        line
                    }],
                    promise: deferredUpdatePromise.promise,
                })
            );

            let resp_code = 200
            fetch(`${this.routes.cart_change_url}`, fetchConfig('json', { body }))
                .then((response) => {
                    console.log('status:cart_change_url', response.status);
                    resp_code = response.status;
                    return response.text();
                })
                .then((responseText) => {
                    if(resp_code === 400) {
                        // 变更行错误情况下，更新购物车行数据
                        console.log("变更行错误情况下，更新购物车行数据");
                        this.refreshCart();
                    } else {
                        this.#handleUpdateResponse({ line, deferredUpdatePromise, responseText });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    deferredUpdatePromise.reject(error);
                    this.dispatchEvent(
                        new CartErrorEvent({
                            error: error?.message || 'Failed to update cart',
                            code: 'SERVICE_UNAVAILABLE'
                        })
                    );
                })
                .finally(() => {
                    this.#enableCartItems();
                    this.#handleRemoveLineSkeleton(line);
                });
        }

        /**
         * 处理购物车更新接口(update.js / change.js)的响应。
         *
         * @param {Object} params - 参数。
         * @param {number} params.line - 购物车行序号。
         * @param {Object} params.deferredUpdatePromise - 延迟 Promise 控制器。
         * @param {string} params.responseText - 接口响应文本。
         */
        #handleUpdateResponse({ line, deferredUpdatePromise, responseText }) {
            const parsedResponseText = JSON.parse(responseText);
            /**
             * update.js: { errors }
             * change.js: { message } / { errors }
             */
            const errors = parsedResponseText?.errors || parsedResponseText?.message;
            if (errors) {
                this.#handleCartError(line, { errors });
                deferredUpdatePromise.reject(new Error(errors));
                return;
            }

            const newSectionHTML = new DOMParser().parseFromString(
                parsedResponseText.sections[this.sectionId],
                'text/html'
            );

            // 从新HTML中获取购物车数量数量元素
            const newCartHiddenItemCount = newSectionHTML.querySelector(this.cartItemCountClass)?.dataset.cartItemCount;
            const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

            // Update data-cart-quantity for all matching variants
            this.#updateQuantitySelectors(parsedResponseText);

            deferredUpdatePromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
                detail: {
                    sections: parsedResponseText.sections,
                    items: parsedResponseText.items,
                    itemCount: newCartItemCount,
                    source: 'jky-cart',
                    didError: false
                }
            });

            this.onCartUpdate(newSectionHTML);
            this.#updateCartQuantitySelectorButtonStates();
        }

        /**
         * 获取需要更新的 sectionId 集合。
         * @returns {Set<string>}
         */
        #getSectionsToUpdate() {
            const sectionsToUpdate = new Set([this.sectionId]);
            document.querySelectorAll(this.mainClass).forEach((item) => {
                if (item instanceof HTMLElement && item.dataset.sectionId) {
                    sectionsToUpdate.add(item.dataset.sectionId);
                }
            });
            return sectionsToUpdate;
        }

        /**
         * 处理购物车错误。
         * @param {number} line - 购物车行序号。
         * @param {Object} parsedResponseText - 接口解析后的响应文本。
         * @param {string} parsedResponseText.errors - 错误信息。
         */
        #handleCartError(line, parsedResponseText) {
            this.refreshCart(line, parsedResponseText.errors);
            const quantitySelector = this.refs.quantitySelectors[line - 1];
            const quantityInput = quantitySelector?.querySelector('input');
            if (!quantityInput) throw new Error('jky cart: Quantity input not found');
            quantityInput.value = quantityInput.defaultValue;

            const cartItemError = this.refs[`cartItemError-${line}`];
            const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
            if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
            if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

            cartItemError.textContent = parsedResponseText.errors;
            cartItemErrorContainer.classList.remove('hidden');

            this.dispatchEvent(
                new CartErrorEvent({
                    error: parsedResponseText.errors || 'Cart update failed',
                    code: 'INVALID'
                })
            );
        }
        /**
         * 处理购物车行的骨架屏状态。
         * @param {number} line - 购物车行序号。
         */
        #handleLineSkeleton(line) {
            const cartItemPrice = this.refs[`cartItemPrice-${line}`];
            if (!(cartItemPrice instanceof HTMLElement)) {
                console.warn('jky cart:Cart item price not found');
                return;
            }
            cartItemPrice.classList.add('skeleton');
        }
        /**
         * 购物车行的骨架屏移除。
         * @param {number} line - 购物车行序号。
         */
        #handleRemoveLineSkeleton(line) {
            const cartItemPrice = this.refs[`cartItemPrice-${line}`];
            if (!(cartItemPrice instanceof HTMLElement)) {
                console.warn('jky cart:Cart item price not found');
                return;
            }
            cartItemPrice.classList.remove('skeleton');
        }

        /**
         * 更新购物车中所有匹配商品变体的数量选择器。
         * @param {Object} updatedCart - 更新后的购物车对象。
         * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - 购物车商品项。
         */
        #updateQuantitySelectors(updatedCart) {
            if (!updatedCart.items) return;
            for (const item of updatedCart.items) {
                const variantId = item.variant_id.toString();
                const selectors = document.querySelectorAll(`jky-quantity-selector[data-variant-id="${variantId}"]`);

                for (const selector of selectors) {
                    const input = selector.querySelector('input[data-cart-quantity]');
                    if (!input) continue;

                    input.setAttribute('data-cart-quantity', item.quantity.toString());
                    // 更新数量选择器的内部状态
                    if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
                        selector.updateCartQuantity();
                    }
                }
            }
        }

        /**
         * 更新所有购物车行数量选择器的按钮状态。
         */
        #updateCartQuantitySelectorButtonStates() {
            for (const selector of document.querySelectorAll('jky-quantity-selector')) {
                selector.updateButtonStates?.();
            }
        }

        /**
         * Handles the line item removal.
         * @param {number} line - The line item index.
         */
        onLineItemRemove(line) {
            this.updateQuantity({
                line,
                quantity: 0,
                action: 'clear'
            });

            const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
            if (!cartItemRowToRemove) return;
            const rowsToRemove = this.active_parent_line ? [
                cartItemRowToRemove,
                // 获取所有待删除行的嵌套子行
                ...Array.from(this.refs.cartItemRows).filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key)
            ] : [cartItemRowToRemove];
   
            rowsToRemove.forEach((row) => {
                const remove = () => row.remove();
                row.style.setProperty('--row-height', `${row.clientHeight}px`);
                row.classList.add('removing');
                onAnimationEnd(row, remove);
            });
        }

        /**
         * 更新购物车DOM。
         * @param {string} html - 更新后的HTML内容。
         * @param {Object} error - 更新附带错误信息
         * @param {number} error.line - 购物车行序号
         * @param {Object} error.parsedResponseText - 错误信息
         */
        onCartUpdate(html, error = null) {
            const selectors = ['scroll-hint .jky-cart-__items-main', 'scroll-hint .jky-cart-__items-other', '.title_cartItemCount', '.jky-cart-__items-top', '.jky-cart-__items-bottom', '.jky-cart-summary', '.jky-cart__ctas'];

            // 处理空购物车状态
            if(html.querySelector('.jky-cart__empty')) {
                this.classList.add('jky-cart--empty');
            } else {
                this.classList.remove('jky-cart--empty');
            }

            for (const selector of selectors) {
                const targetElement = this.querySelector(selector);
                const sourceElement = html.querySelector(selector);
                if (targetElement && sourceElement) {
                    targetElement.replaceWith(sourceElement);
                }
            }
            this.refreshAffirm();

            try {
                if(error) {
                    const { line, parsedResponseText } = error;
                    setTimeout(() => {
                        const cartItemError = this.refs[`cartItemError-${line}`];
                        const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
                        if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
                        if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');
                        cartItemError.textContent = parsedResponseText;
                        cartItemErrorContainer.classList.remove('hidden');
                    }, 0);
                }
            } catch (error) {}
        }

        /**
         * 获取指定购物车行相关联的父/子商品行
         * DOM 结构通过：
         * row.dataset.key
         * row.dataset.parentKey
         * 表示 parent / child relationship。
         *
         * @param {number} line - 当前购物车行索引
         * @returns {HTMLElement[]}
         */
        #getRelatedCartItemRows(line, quantity = 1) {
            const currentRow = this.refs.cartItemRows?.[line - 1];
            if (!(currentRow instanceof HTMLElement)) {
                return [];
            }
            if(!this.active_parent_line) return [currentRow];

            const currentKey = currentRow.dataset.key;
            const currentParentKey = currentRow.dataset.parentKey;
            if (!currentKey) {
                return [currentRow];
            }

            /**
             * 当前行是 child：
             * child:
             *   data-key="child-key"
             *   data-parent-key="parent-key"
             * 找到 parent 后，再找到 parent 的所有 children。
             */
            const parentKey = currentParentKey || currentKey;
            const relatedRows = Array.from(this.refs.cartItemRows).filter((row) => {
                if (!(row instanceof HTMLElement)) return false;
                const rowKey = row.dataset.key;
                const rowParentKey = row.dataset.parentKey;

                return (
                    rowKey === parentKey ||
                    rowParentKey === parentKey
                );
            });

            /**
             * 乐观更新关联商品数量。
             *
             * 注意：
             * 这里仅修改 DOM，不触发 input/change 事件，
             * 避免再次触发 #onQuantityChange。
             */
            relatedRows.forEach((row) => {
                const input = row.querySelector(
                    'jky-quantity-selector input[data-cart-quantity]'
                );
                if (!(input instanceof HTMLInputElement)) return;
                // 乐观更新 input
                const numUnit = Number(row?.dataset?.numUnit) || 1;
                input.value = String(quantity * numUnit);

                // 同步 data-cart-quantity
                input.setAttribute(
                    'data-cart-quantity',
                    String(quantity * numUnit)
                );

                const quantitySelector = input.closest('jky-quantity-selector');
                // 同步 quantity selector 内部状态
                quantitySelector?.updateCartQuantity?.();
                // 同步 +/- 按钮状态
                quantitySelector?.updateButtonStates?.();
            })

            return relatedRows.length ? relatedRows : [currentRow];
        }

        /**
         * 禁用购物车操作。
         */
        #disableCartItems() {
            this.refs.main.classList.add('pointer-events-none');
            const { cartTotal, cartSavings, discountTotal } = this.refs;
            cartTotal?.classList.add('skeleton');
            cartSavings?.classList.add('skeleton');
            discountTotal?.classList.add('skeleton');
        }

        /**
         * 恢复购物车操作。
         */
        #enableCartItems() {
            this.refs.main.classList.remove('pointer-events-none');
            const { cartTotal, cartSavings, discountTotal } = this.refs;
            cartTotal?.classList.remove('skeleton');
            cartSavings?.classList.remove('skeleton');
            discountTotal?.classList.remove('skeleton');
        }

        async fetchCartData() {
            if (this.pendingCartFetch) return this.pendingCartFetch;
            this.pendingCartFetch = (async () => {
                const body = JSON.stringify({
                    sections: this.sectionId,
                    sections_url: window.location.pathname
                });
                const response = await fetch(`${this.routes.cart_update_url}`, fetchConfig('json', { body }));
                if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
                const data = await response.json();
                return data;
            })().finally(() => {
                this.pendingCartFetch = null;
            });

            return this.pendingCartFetch;
        }

        open() {
            this.refs.drawer.show(this.refs.trigger);
            this.classList.add('active');
        }
        close() {
            this.refs.drawer.hide(this.refs.trigger);
            this.classList.remove('active');
        }

        /**
         * 强制刷新购物车数据【针对422超出库存以及旧版本加购触发购物车更新
         * @param {number} line - 错误情况下购物车行索引。
         * @param {string} parsedResponseText - 触发刷新的错误信息。
         */
        refreshCart(line, parsedResponseText) {
            this.fetchCartData().then(data => {
                if(!data.sections[this.sectionId]) return;
                const newSectionHTML = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');
                if(line && parsedResponseText) {
                    this.onCartUpdate(newSectionHTML, { line, parsedResponseText });
                } else {
                    this.onCartUpdate(newSectionHTML);
                    setTimeout(() => { this.open(); }, 20);
                }
                this.#updateCartQuantitySelectorButtonStates();
                try { // 手动刷新购物车icon
                    document.querySelector('jky-cart-icon').renderCartBubble(data.item_count || 0);
                } catch (_) {}
            });
        }

        /**
         * 兼容旧版本购物车加购触发购物车更新
         * @param {resp} resp - 购物车更新响应。
         */
        async fixRefreshCart(resp = null) {
            try {
                let res = null;
                let htmlString = '';
                if(resp.sections) {
                    res = resp;
                } else {
                    res = await resp.text();
                    if(typeof res === 'string') {
                        res = JSON.parse(res);
                    }
                }
                htmlString = res.sections['jky-cart'] || res.sections[this.sectionId];
                const newSectionHTML = new DOMParser().parseFromString(htmlString, 'text/html');
                try { // 手动刷新购物车icon title_cartItemCount
                    const newCartHiddenItemCount = newSectionHTML?.querySelector('.title_cartItemCount')?.dataset?.cartItemCount;
                    document.querySelectorAll('jky-cart-icon').forEach((element) => element.renderCartBubble(newCartHiddenItemCount));
                    // newSectionHTML.querySelectorAll(".jky-cart-items__free-tag").forEach((element) => element.classList.add("hidden"));
                } catch (_) {}
                
                this.onCartUpdate(newSectionHTML);
                setTimeout(() => { this.open(); }, 0);
                this.#updateCartQuantitySelectorButtonStates();
                // this.refreshCart();
            } catch (_) {
                this.refreshCart();
            }
        }
        /**
         * 旧版本兼容处理【待移除】
         * @param {number} line - 错误情况下购物车行索引。
         * @param {string} parsedResponseText - 触发刷新的错误信息。
         */
        renderContents(resp = null) {
            if(window.location.pathname.startsWith('/cart')) return;
            if(resp) {
                this.fixRefreshCart(resp);
            } else {
                this.refreshCart();
            }
        }

        /**
         * 刷新购物车 affirm 数据[FIX:外部加购触发]
         */
        refreshAffirm() {
            if(!this.active_affirm) return;
            const affirmElements = this.querySelectorAll('.affirm-as-low-as');
            const catEl = this.querySelector('.jky-cart__ctas');
            const amount = catEl.dataset.total_price ?? 0;
            if(!catEl) return;
            if (!affirmElements.length) return;
            affirmElements.forEach((element) => {
                if(!element.classList.contains('jky-cart-affirm')) element.remove();
                element.setAttribute('data-amount', amount);
            });
            if (typeof window.affirm?.ui?.refresh === 'function') {
                window.affirm.ui.refresh();
                return;
            }
            this.#loadAffirmScript();
        }

        /**
         * 加载 affirm 脚本，加载成功后再次尝试刷新，最多尝试 2 次
         * @returns {Promise<void>}
         */
        #loadAffirmScript() {
            const MAX_RETRY = 2;
            if (this.#affirmRetryCount >= MAX_RETRY) return Promise.resolve();
            if (this.#affirmScriptPromise) return this.#affirmScriptPromise;

            this.#affirmRetryCount += 1;
            this.#affirmScriptPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn1.affirm.com/js/v2/affirm.js';
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load affirm.js'));
                document.head.appendChild(script);
            }).then(() => {
                this.#affirmScriptPromise = null;
                this.refreshAffirm();
            }).catch(() => {
                this.#affirmScriptPromise = null;
            });

            return this.#affirmScriptPromise;
        }

        /**
         * 获取section id.
         * @returns {string} section id.
         */
        get sectionId() {
            const { sectionId } = this.refs.main.dataset;
            if (!sectionId) throw new Error('Section id missing');
            return sectionId;
        }
    });
}

// 购物车专用 - 推荐商品
customElements.get("jky-cart-recommends-products") || customElements.define("jky-cart-recommends-products", class extends Component {
    constructor() {
        super();
    }
    connectedCallback() {
        super.connectedCallback();
        this.loop = true;
        this.loop = this.querySelectorAll(".swiper-slide").length > 1;
        this.initSwiper();
    }
    initSwiper() {
        this.swiper = new Swiper('.jky-cart-recommends__swiper', {
            spaceBetween: 12,
            slidesPerView: 1.2,
            roundLengths: true,
            loop: this.loop,
            breakpoints: {
                1024: {
                    spaceBetween: 12
                },
            }
        });
    }
})